
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Accordion, Card, Spinner, Alert, Badge, Row, Col, Button } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import NonEditableEmployeePersonalDetails from './NoEditEmployeeDetailsComponent.js';
import NonEditableContractDetails from './NoEditContractDetailsComponent.js';
import EmployeeEditModal from './EmployeeEditModal.js';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { formatSSNForDisplay } from '../../../../utils/ssnUtils';
import PageTitle from '../../../layouts/PageTitle.js';
import moment from 'moment';
import 'moment/locale/sv';

// New separated architecture components
import CompensationCard from './CompensationCard';
import AssignmentCard from './AssignmentCard';
import BankAccountCard from './BankAccountCard';
import TaxProfileCard from './TaxProfileCard';
import VacationBalanceCard from './VacationBalanceCard';

// Lucide React Icons
import {
  ArrowLeft,
  Edit3,
  Plus,
  User,
  Mail,
  Phone,
  CreditCard,
  Building2,
  Calendar,
  DollarSign,
  AlertTriangle,
  Info,
  CheckCircle,
  FileText,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Users,
  Briefcase,
  Download,
  ShieldAlert
} from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import * as gdprApi from '../../../../services/api/gdprApi';

// Set Swedish locale globally for this component
moment.locale('sv');

const ViewEmployee = () => {
  const { companyId, id } = useParams();
  const history = useHistory();
  const [employee, setEmployee] = useState(null);
  const [currentContract, setCurrentContract] = useState(null);
  const [currentCompensation, setCurrentCompensation] = useState(null);
  const [compensationHistory, setCompensationHistory] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [bankAccountHistory, setBankAccountHistory] = useState([]);
  const [currentTaxProfile, setCurrentTaxProfile] = useState(null);
  const [taxProfileHistory, setTaxProfileHistory] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadEmployee = useCallback(async () => {
    const getEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${id}`);
    try {
      const result = await axios.get(getEmployeeApiUrl, { withCredentials: true });
      setEmployee(result.data.data || result.data);
      setError(null);
    } catch (error) {
      setError('Det gick inte att ladda anställd data. Försök igen senare.');
      console.error('Error loading employee:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId, id]);

  const loadCurrentContract = useCallback(async () => {
    try {
      const getEmployeeContractsApiUrl = getApiUrl(`${companyId}/hr/employees/${id}/contracts`);
      const result = await axios.get(getEmployeeContractsApiUrl, { withCredentials: true });

      const contractsArray = Array.isArray(result.data)
        ? result.data
        : (result.data?.data || result.data || []);

      const today = moment().startOf('day');

      // Find the active contract (started and not ended)
      let current = contractsArray.find(contract => {
        const validFrom = moment(contract.valid_from).startOf('day');
        const validTo = contract.valid_to ? moment(contract.valid_to).startOf('day') : null;
        return validFrom.isSameOrBefore(today, 'day') && (!validTo || validTo.isSameOrAfter(today, 'day'));
      });

      // If no active contract, check for upcoming/future contracts
      if (!current && contractsArray.length > 0) {
        // Find the next upcoming contract (closest future start date)
        const futureContracts = contractsArray
          .filter(contract => {
            const validFrom = moment(contract.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        if (futureContracts.length > 0) {
          current = { ...futureContracts[0], _isFuture: true };
        }
      }

      setCurrentContract(current || null);
    } catch (error) {
      console.error('Error loading contract:', error);
    }
  }, [companyId, id]);

  const loadCompensation = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/compensation/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/compensation/history`), { withCredentials: true })
      ]);

      let current = currentRes.data?.data || null;
      const history = historyRes.data?.data || [];

      console.log('Compensation - current from API:', current);
      console.log('Compensation - history from API:', history);

      // If no current compensation, check for future compensation
      if (!current && history.length > 0) {
        const today = moment().startOf('day');
        console.log('No current compensation, checking for future. Today:', today.format('YYYY-MM-DD'));

        const futureCompensation = history
          .filter(comp => {
            const validFrom = moment(comp.valid_from).startOf('day');
            const isAfterToday = validFrom.isAfter(today, 'day');
            console.log(`Checking comp valid_from: ${comp.valid_from}, isAfter today: ${isAfterToday}`);
            return isAfterToday;
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future compensation found:', futureCompensation);

        if (futureCompensation.length > 0) {
          current = { ...futureCompensation[0], _isFuture: true };
          console.log('Setting current compensation to future:', current);
        }
      }

      setCurrentCompensation(current);
      setCompensationHistory(history);
    } catch (error) {
      console.error('Error loading compensation:', error);
      // Don't set error - compensation might not exist yet
    }
  }, [companyId, id]);

  const loadAssignment = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/assignment/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/assignment/history`), { withCredentials: true })
      ]);

      setCurrentAssignment(currentRes.data?.data || null);
      setAssignmentHistory(historyRes.data?.data || []);
    } catch (error) {
      console.error('Error loading assignment:', error);
      // Don't surface — an employee without a contract has no assignment yet.
    }
  }, [companyId, id]);

  const loadBankAccount = useCallback(async () => {
    try {
      const [accountResult, historyResult] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/bank-account`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/bank-account/history`), { withCredentials: true })
      ]);

      let account = accountResult.data?.data || null;
      const history = historyResult.data?.data || [];

      console.log('Bank Account - current from API:', account);
      console.log('Bank Account - history from API:', history);

      // If no current bank account, check for future bank account
      if (!account && history.length > 0) {
        const today = moment().startOf('day');
        const futureBankAccounts = history
          .filter(acc => {
            const validFrom = moment(acc.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future bank accounts found:', futureBankAccounts);

        if (futureBankAccounts.length > 0) {
          account = { ...futureBankAccounts[0], _isFuture: true };
        }
      }

      setBankAccount(account);
      setBankAccountHistory(history);
    } catch (error) {
      console.error('Error loading bank account:', error);
      // Don't set error - bank account might not exist yet
    }
  }, [companyId, id]);

  const loadTaxProfile = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/tax-profile/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/tax-profile/history`), { withCredentials: true })
      ]);

      let current = currentRes.data?.data || null;
      const history = historyRes.data?.data || [];

      console.log('Tax Profile - current from API:', current);
      console.log('Tax Profile - history from API:', history);

      // If no current tax profile, check for future tax profile
      if (!current && history.length > 0) {
        const today = moment().startOf('day');
        const futureTaxProfiles = history
          .filter(profile => {
            const validFrom = moment(profile.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future tax profiles found:', futureTaxProfiles);

        if (futureTaxProfiles.length > 0) {
          current = { ...futureTaxProfiles[0], _isFuture: true };
        }
      }

      setCurrentTaxProfile(current);
      setTaxProfileHistory(history);
    } catch (error) {
      console.error('Error loading tax profile:', error);
      // Don't set error - tax profile might not exist yet
    }
  }, [companyId, id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadEmployee(),
      loadCurrentContract(),
      loadCompensation(),
      loadAssignment(),
      loadBankAccount(),
      loadTaxProfile()
    ]);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadEmployee(),
        loadCurrentContract(),
        loadCompensation(),
        loadAssignment(),
        loadBankAccount(),
        loadTaxProfile()
      ]);
    };
    loadData();
  }, [loadEmployee, loadCurrentContract, loadCompensation, loadAssignment, loadBankAccount, loadTaxProfile]);

  const handleShowEditModal = () => setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);
  const handleSaveChanges = (updatedEmployee) => {
    loadEmployee();
    setShowEditModal(false);
  };

  const InfoCard = ({ icon: Icon, label, value, type = 'text' }) => (
    <div className="p-3 border rounded-lg bg-white" style={{ borderColor: '#e5e7eb' }}>
      <div className="d-flex align-items-center mb-2">
        <div
          className="me-2 d-flex align-items-center justify-content-center"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            backgroundColor: '#f3f4f6',
            color: '#6b7280'
          }}
          aria-hidden="true"
        >
          <Icon size={16} />
        </div>
        <span className="text-muted" style={{ fontSize: '0.875rem' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
        {type === 'email' && value ? (
          <a
            href={`mailto:${value}`}
            className="text-primary text-decoration-none"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Skicka e-post till ${value}`}
          >
            {value}
          </a>
        ) : type === 'phone' && value ? (
          <a
            href={`tel:${value}`}
            className="text-primary text-decoration-none"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Ring ${value}`}
          >
            {value}
          </a>
        ) : (
          value || '-'
        )}
      </div>
    </div>
  );

  const renderEmployeeDetails = () => (
    <div className="row g-3">
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={User} label="Personnummer" value={formatSSNForDisplay(employee.ssn)} />
      </div>
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={Mail} label="E-post" value={employee.email} type="email" />
      </div>
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={Phone} label="Telefon" value={employee.phonenumber} type="phone" />
      </div>
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={MapPin} label="Adress" value={employee.address} />
      </div>
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={MapPin} label="Postort" value={employee.city} />
      </div>
      <div className="col-md-6 col-lg-4">
        <InfoCard icon={MapPin} label="Postnummer" value={employee.postnumber} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Laddar anställd...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid px-0">
        <Alert variant="danger" className="mb-4">
          <AlertTriangle size={20} className="me-2" />
          {error}
          <div className="mt-2">
            <Button variant="outline-danger" size="sm" onClick={handleRefresh}>
              <RefreshCw size={14} className="me-1" />
              Försök igen
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .info-section {
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .section-divider {
          height: 1px;
          background-color:#a6a6a6;
          margin: 1rem 0;
        }
        
        .modern-contract-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          margin-bottom: 1rem;
          overflow: hidden;
          transition: all 0.3s ease;
          background: white;
        }
        
        .modern-contract-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          border-color: #d1d5db;
        }
        
        .past-contract {
          opacity: 0.7;
        }
        
        .contract-gap-warning {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          color: #92400e;
          font-size: 0.875rem;
        }
        
        .contract-section {
          position: relative;
        }
        
        .section-header h6 {
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #6b7280;
          font-weight: 600;
        }
        
        .contract-summary {
          padding: 1.5rem;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .contract-summary:hover {
          background: rgba(0, 0, 0, 0.02);
        }
        
        .contract-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
        }
        
        .contract-dates {
          display: flex;
          align-items: center;
          font-size: 1rem;
        }
        
        .contract-details-summary {
          margin-top: 0.75rem;
        }
        
        .detail-row {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          color: #6b7280;
        }
        
        .contract-expanded {
          border-top: 1px solid #f3f4f6;
          background: #fafbfc;
          padding: 1.5rem;
        }
        
        .detail-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          height: 100%;
        }
        
        .detail-header {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
        }
        
        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .detail-item:last-child {
          border-bottom: none;
        }
        
        .item-label {
          font-size: 0.8rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        
        .item-value {
          font-weight: 500;
          color: #374151;
        }
        
        .contract-description {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
        }
        
        .description-content {
          color: #374151;
          line-height: 1.6;
        }
        
        .modern-salary-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          margin-bottom: 1rem;
          overflow: hidden;
          transition: all 0.3s ease;
          background: white;
        }
        
        .modern-salary-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          border-color: #d1d5db;
        }
        
        .salary-summary {
          padding: 1.5rem;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .salary-summary:hover {
          background: rgba(0, 0, 0, 0.02);
        }
        
        .salary-period h6 {
          margin: 0;
          font-size: 1.1rem;
        }
        
        .amount-flow {
          display: flex;
          align-items: center;
          margin-top: 0.5rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .gross-amount {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #374151;
          font-size: 1rem;
        }
        
        .flow-arrow {
          color: #6b7280;
          font-weight: bold;
          margin: 0 0.5rem;
        }
        
        .net-amount {
          font-weight: 700;
          color: #10b981;
          font-size: 1rem;
        }
        
        .net-percentage {
          font-size: 0.8rem;
          color: #6b7280;
          margin-left: 0.5rem;
        }
        
        .deductions-summary {
          margin-top: 0.5rem;
        }
        
        .salary-expanded {
          border-top: 1px solid #f3f4f6;
          background: #fafbfc;
          padding: 1.5rem;
        }
        
        .salary-detail-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          height: 100%;
          transition: all 0.2s ease;
        }
        
        .salary-detail-card:hover {
          border-color: #d1d5db;
          transform: translateY(-1px);
        }
        
        .salary-detail-card.income {
          border-left: 4px solid #10b981;
        }
        
        .salary-detail-card.deductions {
          border-left: 4px solid #ef4444;
        }
        
        .salary-detail-card.net {
          border-left: 4px solid #3b82f6;
        }
        
        .salary-detail-card.summary {
          border-left: 4px solid #8b5cf6;
        }
        
        .salary-detail-card .detail-item.primary {
          background: #f8fafc;
          margin: -0.5rem -1rem 0.5rem -1rem;
          padding: 0.75rem 1rem;
          border-radius: 4px;
        }
        
        .summary-stats {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
        }
        
        .stat-label {
          font-size: 0.8rem;
          color: #6b7280;
        }
        
        .stat-value {
          font-weight: 500;
          color: #374151;
        }
        
        .salary-notes {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
        }
        
        .notes-content {
          color: #374151;
          line-height: 1.6;
          font-style: italic;
        }
        
        .back-button {
          color: #6b7280 !important;
          text-decoration: none !important;
          transition: all 0.2s ease;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: white;
        }
        
        .back-button:hover {
          color: #374151 !important;
          background: #f9fafb !important;
          border-color: #d1d5db;
        }
        
        .action-button {
          transition: all 0.2s ease;
          border-radius: 8px;
          font-weight: 500;
        }
        
        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      <PageTitle
        motherMenu="Personal"
        activeMenu={`Anställda / ${employee?.firstname} ${employee?.lastname}`}
      />

      <div className="container-fluid px-0">
        {/* Simple Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Link
              to={`/${companyId}/employees`}
              className="back-button d-flex align-items-center me-4"
              aria-label="Tillbaka till anställda lista"
            >
              <ArrowLeft size={16} className="me-2" aria-hidden="true" />
              Tillbaka
            </Link>
            <div>
              <h3 className="mb-1 fw-bold">
                {`${employee.firstname} ${employee.lastname}`}
              </h3>
              <p className="mb-0 text-muted">
                {employee.jobtitle || 'Anställd'}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              className="action-button d-flex align-items-center"
              onClick={async () => {
                try {
                  const res = await gdprApi.exportEmployeeData(companyId, id);
                  const data = res.data?.data || res.data;
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `gdpr-export-${employee.firstname}-${employee.lastname}-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('GDPR-export nedladdad.');
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Kunde inte exportera data.');
                }
              }}
              aria-label="GDPR-export (artikel 15)"
              title="Exportera all sparad PII — GDPR artikel 15 (registerutdrag)"
            >
              <Download size={16} className="me-2" aria-hidden="true" />
              GDPR-export
            </Button>
            <Button
              variant="outline-danger"
              className="action-button d-flex align-items-center"
              onClick={async () => {
                const { value: reason } = await Swal.fire({
                  title: 'Anonymisera anställd?',
                  html: (
                    '<div style="text-align:left;font-size:0.9em">'
                    + '<p><strong>⚠ Oåterkallelig åtgärd.</strong></p>'
                    + '<p>Alla PII-fält (namn, personnummer, email, telefon, bankuppgifter) '
                    + 'ersätts med REDACTED. Anställningsstatus sätts till <code>erased</code>.</p>'
                    + '<p>Bokföringsmaterial bevaras enligt Bokföringslagen 7 kap 2§ '
                    + '(undantag enligt GDPR art. 17(3)(b)).</p>'
                    + '<p>Ange skäl för radering (loggas för audit).</p>'
                    + '</div>'
                  ),
                  input: 'text',
                  inputPlaceholder: 'T.ex. "Begäran från registrerad enligt GDPR art. 17"',
                  inputValidator: (v) => (!v || !v.trim()) ? 'Skäl krävs.' : undefined,
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Anonymisera',
                  cancelButtonText: 'Avbryt',
                  confirmButtonColor: '#dc3545',
                });
                if (!reason) return;
                try {
                  await gdprApi.anonymizeEmployee(companyId, id, { confirm: true, reason });
                  toast.success('Anställd anonymiserad.');
                  history.push(`/${companyId}/employees`);
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Kunde inte anonymisera.');
                }
              }}
              aria-label="Anonymisera anställd (GDPR artikel 17)"
              title="Oåterkallelig radering av PII — GDPR artikel 17 (rätt till radering)"
            >
              <ShieldAlert size={16} className="me-2" aria-hidden="true" />
              Anonymisera
            </Button>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={handleShowEditModal}
              aria-label={`Redigera ${employee.firstname} ${employee.lastname}`}
            >
              <Edit3 size={16} className="me-2" aria-hidden="true" />
              Redigera
            </Button>
          </div>
        </div>

        {/* Personal Information Card */}
        <div className="info-section mb-4" role="region" aria-labelledby="personal-info-heading">
          <div className="p-4">
            <div className="d-flex align-items-center">
              <User size={20} className="me-3 text-primary" aria-hidden="true" />
              <h5 className="mb-0 fw-bold" id="personal-info-heading">Personlig Information</h5>
            </div>
            <div className="section-divider"></div>
            {renderEmployeeDetails()}
          </div>
        </div>

        {/* Contract-dependent sections - only show if contract exists */}
        {currentContract ? (
          <>
            {/* Employment Status Section */}
            <div className="info-section mb-4" role="region" aria-labelledby="employment-status-heading">
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Briefcase size={20} className={`me-3 ${currentContract._isFuture ? 'text-primary' : 'text-success'}`} aria-hidden="true" />
                    <div>
                      <h5 className="mb-0 fw-bold" id="employment-status-heading">Anställningsstatus</h5>
                      <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                        {currentContract._isFuture ? (
                          <>
                            Påbörjar anställning {moment(currentContract.valid_from).format('DD MMM YYYY')}
                          </>
                        ) : (
                          'Aktiv anställning'
                        )}
                      </p>
                    </div>
                  </div>
                  {currentContract._isFuture && (
                    <span className="badge bg-primary" style={{ fontSize: '0.75rem' }}>
                      Kommande
                    </span>
                  )}
                </div>
                <div className="section-divider"></div>

                <div className="row g-3">
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={Calendar}
                      label="Startdatum"
                      value={moment(currentContract.valid_from).format('DD MMM YYYY')}
                    />
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={Calendar}
                      label="Slutdatum"
                      value={currentContract.valid_to ? moment(currentContract.valid_to).format('DD MMM YYYY') : 'Tillsvidare'}
                    />
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={FileText}
                      label="Anställningstyp"
                      value={currentContract.employment_type || currentContract.employmenttype || 'Fast anställning'}
                    />
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={Clock}
                      label="Arbetstid"
                      value={currentContract.working_hours ? `${currentContract.working_hours}%` : (currentContract.work_week_hours ? `${Math.round((currentContract.work_week_hours / 40) * 100)}%` : '-')}
                    />
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={Calendar}
                      label="Semesterdagar"
                      value={currentContract.vacation_days || 25}
                    />
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <InfoCard
                      icon={Info}
                      label="Uppsägningstid"
                      value={currentContract.notice_period || '-'}
                    />
                  </div>
                  {/* worker_category + high_risk_protection intentionally
                      hidden. Backend defaults (tjansteman, 0) apply to all
                      employees. Unhide here when a customer needs arbetare
                      routing or Försäkringskassan §13 exemption. */}
                </div>
              </div>
            </div>

            {/* Current Compensation Card */}
            <CompensationCard
              employeeId={id}
              companyId={companyId}
              currentCompensation={currentCompensation}
              history={compensationHistory}
              currentContract={currentContract}
              onUpdate={loadCompensation}
            />

            {/* Current Assignment Card — placering, chef, kostnadsställe */}
            <AssignmentCard
              employeeId={id}
              companyId={companyId}
              currentAssignment={currentAssignment}
              history={assignmentHistory}
              currentContract={currentContract}
              onUpdate={loadAssignment}
            />

            {/* Bank Account Card */}
            <BankAccountCard
              employeeId={id}
              companyId={companyId}
              bankAccount={bankAccount}
              history={bankAccountHistory}
              currentContract={currentContract}
              onUpdate={loadBankAccount}
            />

            {/* Tax Profile Card */}
            <TaxProfileCard
              employeeId={id}
              companyId={companyId}
              currentTaxProfile={currentTaxProfile}
              history={taxProfileHistory}
              currentContract={currentContract}
              onUpdate={loadTaxProfile}
            />

            {/* Vacation Balance Card — current semesterår at a glance + per-employee edit */}
            <VacationBalanceCard employeeId={id} companyId={companyId} />
          </>
        ) : (
          <div className="info-section mb-4">
            <div className="p-4">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: '80px', height: '80px' }}>
                    <Info size={40} className="text-primary" aria-hidden="true" />
                  </div>
                </div>
                <h5 className="mb-2">Ytterligare information kräver ett kontrakt</h5>
                <p className="text-muted mb-4">
                  För att hantera ersättning, placering, bankkonto och skatteuppgifter måste du först skapa ett anställningskontrakt.
                </p>
                <Link
                  to={`/${companyId}/employee/${id}/newcontract`}
                  className="btn btn-primary btn-lg action-button d-flex align-items-center mx-auto"
                  style={{ width: 'fit-content' }}
                >
                  <Plus size={18} className="me-2" aria-hidden="true" />
                  Skapa kontrakt
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <EmployeeEditModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        employee={employee}
        companyId={companyId}
        onSave={handleSaveChanges}
      />
    </>
  );
};

export default ViewEmployee;




// import React, { useEffect, useState, useCallback } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Accordion, Card, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
// import axios from '../../../../services/AxiosInstance';
// import NonEditableEmployeePersonalDetails from './NoEditEmployeeDetailsComponent.js';
// import NonEditableContractDetails from './NoEditContractDetailsComponent.js';
// import EmployeeEditModal from './EmployeeEditModal.js';
// import { getApiUrl } from '../../../../utils/ApiUtils';
// import PageTitle from '../../../layouts/PageTitle.js';
// import moment from 'moment';
// import 'moment/locale/sv';  // Import Swedish locale

// const ViewEmployee = () => {
//   const { companyId, id } = useParams();
//   const [employee, setEmployee] = useState(null);
//   const [contracts, setContracts] = useState([]);
//   const [salarySpecs, setSalarySpecs] = useState([]);
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentContractId, setCurrentContractId] = useState(null);

//   moment.locale('sv');  // Set Swedish locale

//   const loadEmployee = async () => {
//     const getEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${id}`);
//     try {
//       const result = await axios.get(getEmployeeApiUrl, { withCredentials: true });
//       setEmployee(result.data);
//       setLoading(false);
//     } catch (error) {
//       setError('Failed to load employee data.');
//       setLoading(false);
//     }
//   };

//   const loadContracts = async () => {
//     try {
//       const getEmployeeContractsApiUrl = getApiUrl(`${companyId}/hr/employees/${id}/contracts`);
//       const result = await axios.get(getEmployeeContractsApiUrl, { withCredentials: true });

//       console.log('Contracts:', result.data);

//       const sortedContracts = result.data
//         .map(contract => ({
//           ...contract,
//           valid_from_date: moment(contract.valid_from),
//           valid_to_date: contract.valid_to ? moment(contract.valid_to) : null,
//         }))
//         .sort((a, b) => moment(b.valid_from).valueOf() - moment(a.valid_from).valueOf());

//       const today = moment();
//       const currentContract = sortedContracts.find(contract => {
//         const validFrom = moment(contract.valid_from);
//         const validTo = contract.valid_to ? moment(contract.valid_to) : null;
//         return validFrom.isSameOrBefore(today) && (!validTo || validTo.isSameOrAfter(today));
//       });

//       setContracts(sortedContracts);
//       setCurrentContractId(currentContract?.contract_id || null);
//     } catch (error) {
//       console.error('Error loading contracts:', error);
//       setError('Failed to load contracts.');
//     }
//   };

//   const loadSalarySpecs = async () => {
//     try {
//       const getSalarySpecsApiUrl = getApiUrl(`${companyId}/hr/employees/${id}/salaries`);
//       const result = await axios.get(getSalarySpecsApiUrl, { withCredentials: true });
//       setSalarySpecs(result.data);
//     } catch (error) {
//       console.error('Error loading salary specs:', error);
//       setError('Failed to load salary specifications.');
//     }
//   };

//   useEffect(() => {
//     loadEmployee();
//     loadContracts();
//     loadSalarySpecs();
//   }, []);

//   const handleShowEditModal = () => setShowEditModal(true);
//   const handleCloseEditModal = () => setShowEditModal(false);
//   const handleSaveChanges = (updatedEmployee) => loadEmployee();

//   const renderEmployeeDetails = () => {
//     const InfoRow = ({ label, value }) => (
//       <Row className="mb-3">
//         <Col sm={4} className="text-muted">
//           {label}:
//         </Col>
//         <Col sm={8} className="fw-medium">
//           {value || '-'}
//         </Col>
//       </Row>
//     );

//     return (
//       <div className="px-2">
//         <Row>
//           <Col md={6}>
//             <InfoRow label="Personnummer" value={employee.ssn} />
//             <InfoRow label="E-post" value={employee.email} />
//             <InfoRow label="Telefon" value={employee.phonenumber} />
//           </Col>
//           <Col md={6}>
//             <InfoRow label="Jobbtitel" value={employee.jobtitle} />
//             <InfoRow label="Clearingnummer" value={employee.clearingnumber} />
//             <InfoRow label="Kontonummer" value={employee.accountnumber} />
//           </Col>
//         </Row>
//       </div>
//     );
//   };

//   if (loading) {
//     return (
//       <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
//         <Spinner animation="border" variant="primary" />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
//         <Alert variant="danger">{error}</Alert>
//       </div>
//     );
//   }

//   return (
//     <>
//       <PageTitle
//         motherMenu="Personal"
//         activeMenu={`Anställda / ${employee.firstname} ${employee.lastname}`}
//       />

//       <div className="container-fluid px-0">
//         {/* Header Section */}
//         <div className="d-flex justify-content-between align-items-center bg-light rounded p-3 mb-4">
//           <div className="d-flex align-items-center">
//             <Link to={`/${companyId}/employees`} className="btn btn-link text-dark p-0">
//               <i className="fas fa-arrow-left"></i>
//               <span className="ms-2">Tillbaka</span>
//             </Link>
//             <div className="mx-4" style={{ width: '1px', height: '24px', backgroundColor: '#dee2e6' }}></div>
//             <h4 className="mb-0 fw-bold">{`${employee.firstname} ${employee.lastname}`}</h4>
//           </div>
//           <button className="btn btn-primary px-4" onClick={handleShowEditModal}>
//             <i className="fas fa-edit me-2"></i>
//             Redigera
//           </button>
//         </div>

//         {/* Personal Information Card */}
//         <Card className="mb-4">
//           <Card.Header className="bg-white py-3">
//             <div className="d-flex justify-content-between align-items-center">
//               <h5 className="mb-0 fw-bold">Personlig Information</h5>

//             </div>
//           </Card.Header>
//           <Card.Body>
//             {renderEmployeeDetails()}
//           </Card.Body>
//         </Card>

//         {/* Contracts Section */}
//         <Card className="mb-4">
//           <Card.Header className="bg-white py-3">
//             <div className="d-flex justify-content-between align-items-center w-100">
//               <h5 className="mb-0 fw-bold">Kontrakt</h5>
//               <Link
//                 to={`/${companyId}/employee/${id}/newcontract`}
//                 className="btn btn-primary btn-sm px-3"
//               >
//                 <i className="fas fa-plus me-2"></i>
//                 Nytt kontrakt
//               </Link>
//             </div>
//           </Card.Header>
//           <Card.Body>
//             {contracts.length > 0 ? (
//               <>
//                 <Accordion defaultActiveKey="0">
//                   {contracts.map((contract, i) => {
//                     const isCurrent = contract.contract_id === currentContractId;
//                     return (
//                       <Accordion.Item key={contract.contract_id} eventKey={i}>
//                         <Accordion.Header>
//                           <div className="d-flex align-items-center" style={{ width: '100%' }}>
//                             {isCurrent && (
//                               <Badge bg="success" className="me-3">
//                                 Aktuell
//                               </Badge>
//                             )}
//                             <span className="me-auto">
//                               {moment(contract.valid_from).format('YYYY-MM-DD')} -{' '}
//                               {contract.valid_to
//                                 ? moment(contract.valid_to).format('YYYY-MM-DD')
//                                 : 'Tillsvidare'}
//                             </span>
//                           </div>
//                         </Accordion.Header>
//                         <Accordion.Body className="pt-4">
//                           <NonEditableContractDetails contractDetails={contract} />
//                         </Accordion.Body>
//                       </Accordion.Item>
//                     );
//                   })}
//                 </Accordion>
//                 {currentContractId === null && (
//                   <Alert variant="warning" className="mt-3">
//                     <i className="fas fa-exclamation-triangle me-2"></i>
//                     Det finns ingen aktiv anställning för denna person.
//                   </Alert>
//                 )}
//               </>
//             ) : (
//               <Alert variant="info">
//                 <i className="fas fa-info-circle me-2"></i>
//                 Personen har ingen anställning registrerad i systemet.
//               </Alert>
//             )}
//           </Card.Body>
//         </Card>

//         {/* Salary Specifications Section */}
//         <Card className="mb-4">
//           <Card.Header className="bg-white py-3">
//             <h5 className="mb-0 fw-bold">Lönespecifikationer</h5>
//           </Card.Header>
//           <Card.Body>
//             {salarySpecs.length > 0 ? (
//               <Accordion defaultActiveKey="0">
//                 {salarySpecs.map((spec, i) => (
//                   <Accordion.Item key={i} eventKey={`${i}`}>
//                     <Accordion.Header>
//                       {moment(spec.valid_from).format('MMMM YYYY')}
//                     </Accordion.Header>
//                     <Accordion.Body>{spec.gross_pay}</Accordion.Body>
//                   </Accordion.Item>
//                 ))}
//               </Accordion>
//             ) : (
//               <Alert variant="info">
//                 <i className="fas fa-info-circle me-2"></i>
//                 Inga lönespecifikationer finns registrerade.
//               </Alert>
//             )}
//           </Card.Body>
//         </Card>
//       </div>

//       <EmployeeEditModal
//         show={showEditModal}
//         handleClose={handleCloseEditModal}
//         employee={employee}
//         companyId={companyId}
//         onSave={handleSaveChanges}
//       />

//       <style>{`
//         .fw-medium {
//           font-weight: 500 !important;
//         }
//       `}</style>
//     </>
//   );
// };

// export default ViewEmployee;